import { useState, useEffect, useRef, useCallback } from "react";
import { Video, VideoOff, Mic, MicOff, PhoneOff, X, Maximize2, Minimize2 } from "lucide-react";

/**
 * IMVideoCallWindow - A floating, draggable video call window for private messages
 * This provides a better UX than embedding video in the small PM window
 */
const IMVideoCallWindow = ({
  targetUserId,
  targetUsername,
  localStream,
  remoteStream,
  isTestMode,
  webcamConnected,
  onStartCall,
  onEndCall,
  onClose
}) => {
  const [position, setPosition] = useState({ x: 200, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const windowRef = useRef(null);

  // Attach local stream to video element
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Attach remote stream to video element
  useEffect(() => {
    if (remoteStream && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Handle drag start
  const handleMouseDown = (e) => {
    if (e.target.closest('.video-title-bar')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // Handle drag move
  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 300, e.clientY - dragOffset.y))
      });
    }
  }, [isDragging, dragOffset]);

  // Handle drag end
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Toggle audio
  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  return (
    <div
      ref={windowRef}
      className="fixed z-[9999] shadow-2xl"
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
      data-testid={`im-video-call-${targetUserId}`}
    >
      <div className="retro-window" style={{ width: isMinimized ? 200 : 380 }}>
        {/* Title Bar - Draggable */}
        <div className="video-title-bar retro-title-bar cursor-move flex justify-between items-center">
          <span className="text-xs flex items-center gap-1">
            <Video className="w-3 h-3" />
            Video Call - {targetUsername}
          </span>
          <div className="flex gap-1">
            <button
              className="retro-button px-1 py-0 text-xs"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </button>
            <button
              className="close-button-visible"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Video Content */}
        {!isMinimized && (
          <div className="bg-black p-2">
            {/* Video Grid */}
            <div className={`grid ${isTestMode ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
              {/* Local Video */}
              <div className="relative">
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full aspect-video object-cover rounded bg-gray-900"
                  style={{ transform: 'scaleX(-1)' }}
                />
                <span className="absolute bottom-1 left-1 text-[10px] text-white bg-black/70 px-1.5 py-0.5 rounded">
                  {isTestMode ? '📷 Preview' : 'You'}
                </span>
                {localStream && (
                  <span className="absolute top-1 right-1 text-[8px] text-white bg-red-600 px-1.5 py-0.5 rounded animate-pulse">
                    LIVE
                  </span>
                )}
              </div>

              {/* Remote Video */}
              {!isTestMode && (
                <div className="relative bg-gray-900 aspect-video rounded flex items-center justify-center">
                  {remoteStream ? (
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <div className="text-center">
                      <Video className="w-8 h-8 text-gray-600 mx-auto mb-1" />
                      <span className="text-xs text-gray-500">
                        {webcamConnected ? 'Connected' : 'Waiting...'}
                      </span>
                    </div>
                  )}
                  <span className="absolute bottom-1 left-1 text-[10px] text-white bg-black/70 px-1.5 py-0.5 rounded">
                    {targetUsername}
                  </span>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex justify-center items-center gap-2 mt-3 pb-1">
              {isTestMode ? (
                <>
                  <button
                    className="retro-button px-4 py-1.5 bg-green-200 hover:bg-green-300 flex items-center gap-1 text-xs"
                    onClick={onStartCall}
                    data-testid="start-call-btn"
                  >
                    <Video className="w-4 h-4 text-green-700" />
                    Start Call
                  </button>
                  <button
                    className="retro-button px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-xs"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {/* Mute Toggle */}
                  <button
                    className={`retro-button p-2 ${isMuted ? 'bg-red-200' : 'bg-gray-200'} hover:bg-opacity-80`}
                    onClick={toggleMute}
                    title={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <MicOff className="w-4 h-4 text-red-600" />
                    ) : (
                      <Mic className="w-4 h-4 text-green-600" />
                    )}
                  </button>

                  {/* Video Toggle */}
                  <button
                    className={`retro-button p-2 ${isVideoOff ? 'bg-red-200' : 'bg-gray-200'} hover:bg-opacity-80`}
                    onClick={toggleVideo}
                    title={isVideoOff ? "Turn on camera" : "Turn off camera"}
                  >
                    {isVideoOff ? (
                      <VideoOff className="w-4 h-4 text-red-600" />
                    ) : (
                      <Video className="w-4 h-4 text-green-600" />
                    )}
                  </button>

                  {/* End Call */}
                  <button
                    className="retro-button p-2 bg-red-500 hover:bg-red-600"
                    onClick={onEndCall}
                    title="End Call"
                    data-testid="end-call-btn"
                  >
                    <PhoneOff className="w-4 h-4 text-white" />
                  </button>
                </>
              )}
            </div>

            {/* Connection Status */}
            {!isTestMode && (
              <div className="text-center mt-1">
                <span className={`text-[10px] ${webcamConnected ? 'text-green-400' : 'text-yellow-400'}`}>
                  {webcamConnected ? '● Connected' : '○ Connecting...'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Minimized View */}
        {isMinimized && (
          <div className="bg-[#C0C0C0] p-2 text-center">
            <span className="text-xs">
              {isTestMode ? '📷 Camera Preview' : webcamConnected ? '🟢 Video Active' : '🟡 Connecting...'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default IMVideoCallWindow;
